import _ from 'lodash';
import {
  processCoupon,
  getInitialDiscountResult
} from './utils';
import {
  couponFetchStatus,
  couponSourceTypeMap,
  PROMOTION_TYPE,
} from './constant';
import { isEmptyVal } from '@/utils';
import { compareDate, getNextPstCycleDate } from '@/utils/dateformat';
import { BILLING_CYCLE } from '@/common/constants';
import { getPlansMap } from '@/common/product';
import { couponStrategyFactory } from '../factory';
import { measureDiscountCalculation } from './performance-monitor';
import { logger } from '@/utils/log';

/**
 * Discount calculation service class
 * Contains pure functions for all discount calculations
 *
 * @typedef {import('@/types/Base').CouponDiscountMapResultBean} CouponDiscountMapResultBean
 * @typedef {import('@/types/Base').PlanItemBean} PlanItemBean
 * @typedef {import('@/types/Base').SingleAmountPlansMapBean} SingleAmountPlansMapBean
 * @typedef {import('@/types/Base').PromotionDiscountInfoBean} PromotionDiscountInfoBean
 * @typedef {import('@/types/Base').PromotionInfoBean} PromotionInfoBean
 * @typedef {import('@/types/Base').BundleInfoBean} BundleInfoBean
 * @typedef {import('@/types/Base').DiscountPlanBean} DiscountPlanBean
 * @typedef {import('@/types/Base').ProcessCouponParamsBean} ProcessCouponParamsBean
 * @typedef {import('@/types/Base').CouponState} CouponState
 * @typedef {import('@/types/Base').SelectedPlansAmount} SelectedPlansAmount
 * @typedef {import('@/types/Base').NextBillAmount} NextBillAmount
 */
export class DiscountCalculationService {
  /**
   * Calculate discount based on coupon type using strategy factory pattern
   * @param {CouponState} couponState - Coupon state object
   * @param {Array<PlanItemBean>} selectedPlans - Selected plans array
   * @param {SingleAmountPlansMapBean} planWithAmountMap - Plan amount mapping
   * @param {string} verifyCouponStatus - Coupon verification status
   * @param {Object} allGetters - All store getters (for complex calculations)
   * @returns {CouponDiscountMapResultBean} Discount result based on coupon type
   */
  static calculateCouponDiscount(couponState = {}, selectedPlans = [], planWithAmountMap = {}, verifyCouponStatus = couponFetchStatus.idle, allGetters = {}) {
    try {
      // Wrap with performance monitoring and use strategy factory
      return measureDiscountCalculation(() => {
        return couponStrategyFactory.calculateDiscount(
          couponState,
          selectedPlans,
          planWithAmountMap,
          verifyCouponStatus,
          allGetters
        );
      });
    } catch (error) {
      // Error boundary: catch any unexpected errors from strategy calculation
      logger.error('[DiscountCalculationService] Unexpected error in calculateCouponDiscount:', {
        error: error.message,
        stack: error.stack,
        couponType: couponState?.couponType,
        couponSourceType: couponState?.couponSourceType,
        selectedPlansCount: selectedPlans?.length,
      });
      // Return safe default result to prevent UI crash
      return getInitialDiscountResult();
    }
  }

  /**
   * Calculate the next bill discount for each billing cycle
   * @param {Object} planWithDiscount - Discount info for each plan
   * @param {Object} selectedPlanMap - Selected plan details
   * @param {Object} options - Configuration options
   * @param {boolean} options.isPendingCoupon - Whether the coupon is pending (skip expireDate check if true)
   * @returns {Object} Discount summary for each billing cycle
   */
  static calculateNextBillDiscount(planWithDiscount = {}, selectedPlanMap = {}, options = {}) {
    const { isPendingCoupon = false } = options;

    // Initialize discount result for each billing cycle
    const discountResult = getInitialDiscountResult();

    // Get next cycle dates with PDT offset -7
    const nextCycleDates = getNextPstCycleDate({ offset: -7 });

    // Map billing cycle to result key and next cycle date
    const cycleMap = {
      [BILLING_CYCLE.MONTHLY]: { key: 'monthlyDiscount', nextDate: nextCycleDates.nextMonthly },
      [BILLING_CYCLE.ANNUAL]: { key: 'annualDiscount', nextDate: nextCycleDates.nextAnnual },
      [BILLING_CYCLE.QUARTERLY]: { key: 'quarterlyDiscount', nextDate: nextCycleDates.nextQuarterly },
      [BILLING_CYCLE.TWO_YEARS]: { key: 'twoYearsDiscount', nextDate: nextCycleDates.nextTwoYears },
      [BILLING_CYCLE.THREE_YEARS]: { key: 'threeYearsDiscount', nextDate: nextCycleDates.nextThreeYears },
      [BILLING_CYCLE.ANNUAL_TERM_MONTHLY]: { key: 'annualTermMonthlyDiscount', nextDate: nextCycleDates.nextAnnual },
    };

    // Iterate all plans with discount
    _.forEach(planWithDiscount, (discountInfo, planCode) => {
      const curPlan = _.get(selectedPlanMap, planCode, {});
      const isOneMonth = _.get(curPlan, 'isOneMonth', false);
      const billingCycle = _.get(curPlan, 'billing_cycle');
      if (isOneMonth || billingCycle === BILLING_CYCLE.ONE_TIME) return; // Skip one month/one time plans

      const { expireDate, forever = false, discountAmount = 0 } = discountInfo;
      const cycleConf = cycleMap[billingCycle];
      if (!cycleConf) return;

      // If pending coupon, always add discount without checking expireDate
      // If forever, always add discount; else, check expire date
      if (isPendingCoupon || forever || compareDate(expireDate, cycleConf.nextDate)) {
        discountResult[cycleConf.key] += discountAmount;
      }
    });

    return discountResult;
  }

  /**
   * Calculate the next bill amount for each billing cycle after applying all discounts
   * @param {SelectedPlansAmount} selectedPlansAmount - Selected plans amount object
   * @param {Object} promotionDiscountInfoMap - Promotion discount info map
   * @param {Object} discountWithAmountMap - Coupon discount info map
   * @param {Array<PlanItemBean>} selectedPlans - Selected plans array
   * @returns {NextBillAmount} Object containing discount and charge details for each billing cycle
   */
  static calculateNextBillAmount(selectedPlansAmount = {}, promotionDiscountInfoMap = {}, discountWithAmountMap = {}, selectedPlans = [], couponState = {}) {
    // Define billing cycle mappings for configuration-driven processing
    const cycleConfig = [
      { amount: 'monthlyAmount', discount: 'monthlyDiscount', charge: 'monthlyCharge' },
      { amount: 'annualAmount', discount: 'annualDiscount', charge: 'annualCharge' },
      { amount: 'quarterlyAmount', discount: 'quarterlyDiscount', charge: 'quarterlyCharge' },
      { amount: 'twoYearsAmount', discount: 'twoYearsDiscount', charge: 'twoYearsCharge' },
      { amount: 'threeYearsAmount', discount: 'threeYearsDiscount', charge: 'threeYearsCharge' },
      { amount: 'annualTermMonthlyAmount', discount: 'annualTermMonthlyDiscount', charge: 'annualTermMonthlyCharge' },
    ];

    const selectedPlanMap = getPlansMap(selectedPlans);

    // Get all discount lists from promotions and coupons
    const allDiscountLists = [
      ...this.getNextBillDiscountListForPromotion(promotionDiscountInfoMap, selectedPlanMap),
      ...this.getNextBillDiscountListForCoupon({ discountWithAmountMap, selectedPlanMap, couponState }),
    ];
    // Aggregate all discounts and calculate charges for each billing cycle
    const discountMap = getInitialDiscountResult();
    const charges = {};

    cycleConfig.forEach(({ amount, discount, charge }) => {
      // Sum up discounts for each billing cycle
      discountMap[discount] = _.sumBy(allDiscountLists, item => item[discount] || 0);
      // Calculate final charge after applying discount
      charges[charge] = (selectedPlansAmount[amount] || 0) - discountMap[discount];
    });

    // Calculate totals by summing only the numeric discount and charge fields from cycleConfig
    const totalDiscount = _.sumBy(cycleConfig, ({ discount }) => discountMap[discount]);
    const totalChargeAmount = _.sumBy(cycleConfig, ({ charge }) => charges[charge]);
    const totalBeforeDiscountChargeAmount = _.sumBy(cycleConfig, ({ amount }) => selectedPlansAmount[amount] || 0);
    return { ...discountMap, ...charges, totalDiscount, totalChargeAmount, totalBeforeDiscountChargeAmount };
  }

  /**
   * Get the discount list for the next bill from all promotion discounts
   * @param {Object} promotionDiscountInfoMap - Promotion discount info map
   * @param {Array<PlanItemBean>} selectedPlans - Selected plans array
   * @returns {Array} List of discount summaries for each billing cycle from promotions
   */
  static getNextBillDiscountListForPromotion(promotionDiscountInfoMap = {}, selectedPlanMap = {}) {
    // Iterate over each promotion discount item and compute the next bill discount if available
    return _.chain(promotionDiscountInfoMap)
      .values()
      .map((item) => {
        const planWithDiscount = item.discountPlans;
        // Skip if discountPlans is empty
        if (isEmptyVal(planWithDiscount)) return undefined;
        // Promotion discounts don't need pending coupon check, pass empty options
        return this.calculateNextBillDiscount(planWithDiscount, selectedPlanMap, {});
      })
      .filter(Boolean) // Remove undefined results
      .value();
  }

  /**
   * Get the discount list for the next bill from coupon discounts
   * @param {Object} params - Parameters object
   * @param {Object} params.discountWithAmountMap - Coupon discount info map
   * @param {Object} params.selectedPlanMap - Selected plan details map
   * @param {CouponState} params.couponState - Coupon state object (to check if pending and source type)
   * @returns {Array} List containing discount summary for each billing cycle from coupon
   */
  static getNextBillDiscountListForCoupon(params = {}) {
    const {
      discountWithAmountMap = {},
      selectedPlanMap = {},
      couponState = {},
    } = params;

    const planWithDiscount = discountWithAmountMap.discountPlans;
    const couponSourceType = couponState.couponSourceType || '';

    // Only process if coupon is not threshold type and discountPlans is not empty
    if (couponSourceType !== couponSourceTypeMap.threshold && !isEmptyVal(planWithDiscount)) {
      const isPendingCoupon = couponState.isPendingCoupon || false;
      return [this.calculateNextBillDiscount(planWithDiscount, selectedPlanMap, { isPendingCoupon })];
    }
    return [];
  }

  /**
   * Calculate discount info for soft bundle promotion
   * @param {Object} bundleInfo - Soft bundle information
   * @param {Array<PlanItemBean>} selectedPlans - Selected plans array
   * @param {SingleAmountPlansMapBean} planWithAmountMap - Plan amount mapping
   * @returns {CouponDiscountMapResultBean} Discount result for soft bundle
   */
  static calculatePromotionDiscountInfoForSoftBundle(bundleInfo = {}, selectedPlans = [], planWithAmountMap = {}) {
    if (isEmptyVal(bundleInfo)) {
      return getInitialDiscountResult();
    }

    const { discountPlanList } = bundleInfo;

    return processCoupon({
      discountPlanList,
      selectedPlanList: selectedPlans,
      planWithAmountMap,
    });
  }

  /**
   * Compute discount info map for all promotion types
   * @param {Object} promotionInfo - Promotion information object
   * @param {Array<PlanItemBean>} selectedPlans - Selected plans array
   * @param {SingleAmountPlansMapBean} planWithAmountMap - Plan amount mapping
   * @returns {Object} Discount info map for each promotion type
   */
  static computePromotionDiscountInfoMap(promotionInfo = {}, selectedPlans = [], planWithAmountMap = {}) {
    const result = {};

    // Process softBundle promotion type
    const softBundleInfo = promotionInfo[PROMOTION_TYPE.softBundle];
    if (softBundleInfo) {
      const isActive = [couponFetchStatus.pending, couponFetchStatus.successfully]
        .includes(softBundleInfo.status);

      result[PROMOTION_TYPE.softBundle] = isActive
        ? this.calculatePromotionDiscountInfoForSoftBundle(softBundleInfo, selectedPlans, planWithAmountMap)
        : getInitialDiscountResult();
    }

    return result;
  }
}
